import { useCallback, useEffect, useRef, useState } from "react";
import { getNotifications, type AppNotification } from "@/lib/notifications";

/**
 * Desktop alerts — the toast the operating system draws outside the browser.
 *
 * ⚠️ **IT IS NOT A CHANNEL, and everything about this file follows from that.** Nothing is
 * composed for it and no row is written for it: it RENDERS in-app notifications the signed-in
 * person is already entitled to, read from the same endpoint the bell reads. So D-17's rule
 * that a payload is computed per recipient is untouched, D-48's one-row-per-channel is
 * untouched, and the worst this can do is draw a toast for something already sitting on the
 * bell behind it. There is no server-side decision here to get wrong.
 *
 * ⚠️ **WHAT THE TOAST MAY SAY IS BOUNDED BY WHERE IT APPEARS.** It is drawn by the operating
 * system, over whatever is on screen, on a machine that may be unlocked in a corridor — which
 * is the exposure D-114 keeps a request's subject out of an EMAIL SUBJECT LINE for, in as many
 * words: *that line is readable on a lock screen and over a shoulder*. So it carries
 * `subject` — the message's own reference line, the string already written to survive an inbox
 * — and never `ticketSubject` or `requesterName`, both of which the bell does draw because the
 * bell is behind a session on a screen somebody is already looking at.
 *
 * ⚠️ **THREE CONDITIONS, ALL REQUIRED.** The `notify.browser` flag, the person's own switch,
 * and the browser's permission — which is granted per browser and per machine, and which only
 * a real gesture can request. Any one of them absent and this does nothing at all.
 */

/** What the browser will tell us about permission, plus the case where it has no idea. */
export type DesktopPermission = "unsupported" | "default" | "granted" | "denied";

export function desktopPermission(): DesktopPermission {
  // ⚠️ Feature-detected rather than assumed. `Notification` is absent in an insecure context,
  // in some embedded webviews, and on iOS Safari outside a home-screen app — and reading
  // `Notification.permission` on any of those throws, which would take down whatever screen
  // was rendering this.
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";

  return Notification.permission as DesktopPermission;
}

/**
 * Asks the browser for permission, returning what it decided.
 *
 * ⚠️ MUST be called from a real user gesture. Browsers refuse — and Chrome permanently blocks
 * the origin — when a page asks unprompted on load, so this is wired to a switch somebody
 * presses and to nothing else.
 */
export async function requestDesktopPermission(): Promise<DesktopPermission> {
  if (desktopPermission() === "unsupported") return "unsupported";

  try {
    return (await Notification.requestPermission()) as DesktopPermission;
  } catch {
    // Some browsers reject rather than resolving "denied". Either way nothing is granted.
    return desktopPermission();
  }
}

/** How often to look for new notifications while the tab is in the background. */
const POLL_MS = 60_000;

/**
 * The most notifications one pass may draw.
 *
 * ⚠️ A cap rather than a queue. Somebody returning from a two-hour meeting with forty unread
 * items does not want forty toasts stacked over their screen, and the bell they are about to
 * open has all of them. Bounded to the newest few, which is what a desktop alert is for.
 */
const MAX_PER_PASS = 3;

/**
 * Raises a desktop alert for each in-app notification that arrives while this tab is open.
 *
 * ⚠️ **IT POLLS, AND THE BELL DELIBERATELY DOES NOT.** `NotificationsBell` reads on focus and
 * on `visibilitychange` and says in as many words that a request per person per interval
 * across ~2,600 people is not worth a number that changes when a clock passes an hour. That
 * reasoning is intact and this is not an exception to it: a desktop alert is only useful when
 * the tab is NOT in front of somebody, which is exactly when the bell's signals never fire —
 * so the feature is impossible without a poll. What bounds the cost is who pays it: the poll
 * runs only for people who have granted permission and turned it on, i.e. people who have
 * asked to be interrupted. Everybody else makes no extra request at all.
 *
 * ⚠️ **The first pass draws nothing.** It records what already exists so that opening the app
 * does not fire a toast for every unread item from yesterday; only what appears AFTER that is
 * announced. The set of announced ids is per tab and dies with it, which is correct — two tabs
 * open is two places somebody might be looking, and neither can know what the other drew.
 *
 * ⚠️ Failures are swallowed. This runs on every screen in the product, and a blip on a
 * background poll must not surface an error over whatever somebody is actually doing — the
 * same rule the bell applies to its own fetch.
 */
export function useDesktopNotifications(enabled: boolean) {
  // ⚠️ Ids that have already been drawn, plus a flag for the first pass. Refs rather than
  // state: nothing here renders, and a re-render per poll on every screen in the product
  // would be a cost paid for nothing.
  const seen = useRef<Set<string>>(new Set());
  const primed = useRef(false);

  const announce = useCallback((items: AppNotification[]) => {
    // Newest first is how the endpoint returns them; drawing them oldest-first means the most
    // recent alert is the one left on screen.
    // ⚠️ `item.desktop` is the server's answer to "did this person ask to be interrupted for
    // this kind" (D-155) — their own Desktop column on the profile, defaults applied, and
    // always true for a reply, a closure or an access change. Decided there, not here: the
    // key a bell item was composed under is not on the wire and there is no second list of
    // event names in this file to keep in step with the server's.
    const fresh = items
      .filter((item) => item.readAtUtc === null && item.desktop && !seen.current.has(item.id))
      .slice(0, MAX_PER_PASS)
      .reverse();

    for (const item of fresh) {
      try {
        // ⚠️ THE MESSAGE'S OWN SUBJECT LINE AND NOTHING ELSE. Never `ticketSubject`, never
        // `requesterName` — see this module's header. `tag` is the notification id, so a
        // browser that fires this twice replaces the toast rather than stacking two.
        const toast = new Notification("ARS Service Desk", {
          body: item.subject,
          tag: item.id,
        });

        toast.onclick = () => {
          window.focus();

          // ⚠️ The OPAQUE number (D-4), and `/todos` when there is none — a follow-up reminder
          // names no request (D-95). Decided from the ABSENT number rather than from the event
          // name, exactly as the bell decides it, so there is no second list of event names to
          // keep in step with the server's.
          window.location.href = item.number ? `/tickets/${item.number}` : "/todos";
          toast.close();
        };
      } catch {
        // Constructing a Notification throws in a few browsers when the page is not the
        // active one, and on any platform that requires a service worker. Nothing to do:
        // the item is on the bell either way.
      }
    }

    // Every item is marked seen, the ones not drawn included: a kind switched on later must
    // not announce yesterday's backlog of it on the next pass.
    for (const item of items) seen.current.add(item.id);
  }, []);

  useEffect(() => {
    if (!enabled) {
      // ⚠️ Reset, so switching it off and on again does not silently announce nothing: the
      // next first pass primes afresh rather than treating a stale set as already-drawn.
      primed.current = false;
      seen.current = new Set();
      return;
    }

    let cancelled = false;

    const pass = async () => {
      try {
        const page = await getNotifications();
        if (cancelled) return;

        if (!primed.current) {
          // The first pass records and draws nothing — see the remarks above.
          for (const item of page.items) seen.current.add(item.id);
          primed.current = true;
          return;
        }

        announce(page.items);
      } catch {
        // Swallowed. A background poll must never surface an error on the screen somebody
        // is using.
      }
    };

    void pass();

    const handle = window.setInterval(() => void pass(), POLL_MS);

    // Coming back to the tab is both the cheapest signal that something may have arrived and
    // the moment a toast is least useful — so this refreshes the seen set without drawing,
    // via the same pass, because anything unread is about to be visible on the bell anyway.
    const onVisible = () => {
      if (document.visibilityState === "visible") void pass();
    };

    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(handle);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, announce]);
}

/**
 * The browser's permission state, kept current across a grant.
 *
 * ⚠️ `Notification.permission` is a snapshot and does not raise an event, so a component
 * reading it once renders "not allowed" for ever after somebody allows it. This re-reads on
 * focus, which is when a permission dialog has just been dismissed.
 */
export function useDesktopPermission(): [DesktopPermission, (next: DesktopPermission) => void] {
  const [permission, setPermission] = useState<DesktopPermission>(() => desktopPermission());

  useEffect(() => {
    const refresh = () => setPermission(desktopPermission());

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);

    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  return [permission, setPermission];
}
